// Wrapped2025.jsx
import React, { useEffect, useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { fetchUserWrapped } from '../apis/wrappedApi';
import { useParams } from 'react-router-dom';

const STORY_WIDTH = 1080;
const STORY_HEIGHT = 1920;

export default function Wrapped() {
  const { userId, year } = useParams();
  const [stats, setStats] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        if (!userId || !year) return;

        const response = await fetchUserWrapped(userId, year);
        if (response.status !== 200) throw new Error('Network response was not ok');
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch wrapped stats', err);
      }
    }

    fetchStats();
  }, [userId, year]);

 
  const handleDownload = async () => {
    if (!containerRef.current || isExporting) return;
    setIsExporting(true);
    // wait for React to re-render and remove buttons from the DOM
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const canvas = await html2canvas(containerRef.current, {
        width: STORY_WIDTH,
        height: STORY_HEIGHT,
        scale: 1,
        useCORS: true,
        backgroundColor: null,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `GP_Wrapped_${year}_${userId}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleShareInstagram = async () => {
    if (!containerRef.current || isExporting) return;
    setIsExporting(true);
    // ensure buttons are removed from DOM before taking the snapshot
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      // 1. Convert the wrapped to PNG using html2canvas
      const canvas = await html2canvas(containerRef.current, {
      width: STORY_WIDTH,
      height: STORY_HEIGHT,
      scale: 1,
      useCORS: true,
      backgroundColor: null,
    });

      // 2. Convert to blob (required for navigator.share on mobile)
      const dataUrl = canvas.toDataURL("image/png");
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], "gp_wrapped_2025.png", { type: "image/png" });

      // 3. Use Web Share API (mobile only)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My GP Wrapped 2025",
          text: "Check out my year at GP! 💗💪",
        });
        return;
      }

      alert("Your device does not support direct Instagram sharing. Try downloading instead.");
    } catch (err) {
      console.log("Error sharing to Instagram:", err);
      alert("Unable to share to Instagram on this device.");
    } finally {
      setIsExporting(false);
    }
  };


  if (!stats) {
    return <div>Loading your 2025 Wrapped…</div>;
  }

  if (!stats.has_activity) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h1>Your GP Wrapped {year}</h1>
        <p>This year was just the beginning.</p>
        <p>Let’s make {year+1} your strongest year 💪</p>
      </div>
    );
  }

  const totalClasses = stats.total_classes ?? 0;
  const favoriteClass =
    stats.favorite_classes?.[0]?.favorite_class || '—';
  const favoriteClassCount = 
    stats.favorite_classes?.[0]?.total_taken || 0;
  const topMonth = stats.top_month || '—';
  const bestRanking =
    stats.best_ranking ? `#${stats.best_ranking}` : '—';

  return (
    <>
    <div
      className="gp-wrapped"
      style={{
        width:'100dvw',
        height: '100dvh',
        backgroundImage: 'url(/img/brand/gp_loto_phone.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: '#fff',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 🔥 BACKGROUND OVERLAY */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            linear-gradient(
              to bottom,
              rgba(0,0,0,0) 45%,
              rgba(0,0,0,0.35) 70%,
              rgba(0,0,0,0.8) 100%
            )
          `,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      {/* CONTENT */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <style>{`
          .bold { font-weight: 700 !important; }
        `}</style>
        
        {/* HEADER */}
        <div className='text-center mb-3'>
          <div
            style={{
              marginTop: '16vh',
            }}
          >
          </div>
          <h1 className='mt-4' style={{fontSize: '55px'}}>
            Wrapped {year}
          </h1>
        </div>

        {/* MAIN STAT */}
        <div className='text-center mb-5'>
          <h1 className='bold'>
            {totalClasses}
          </h1>
          <h3>
            Clases tomadas
          </h3>
        </div>

        {/* HIGHLIGHT CARDS */}
        <div
          style={{
            width: '95%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridGap: '20px',
            marginBottom: '100px',
          }}
        >
          <Card title="Clase Favorita" value={favoriteClass} footer={favoriteClassCount + " clases"} />
          <Card title="Mejor Mes" value={topMonth} />
          {stats.best_ranking < 10 && (
            <Card
              title="Mejor Ranking"
              value={bestRanking}
              style={{ gridColumn: '1 / -1', justifySelf: 'center' }}
            />
          )}
        </div>

        {/* FOOTER */}
        <div
          style={{
            position: 'absolute',
            bottom: '40px',
            textAlign: 'center',
            width: '100%',
            fontSize: '24px',
            color: 'white',
          }}
        >
          Gracias por ser parte de la comunidad GP 💗
        </div>

        {!isExporting && (
          <button
            onClick={handleShareInstagram}
            style={{
              position: 'absolute',
              bottom: '80px',
              padding: '14px 26px',
              fontSize: '18px',
              backgroundColor: '#f5a',
              border: 'none',
              borderRadius: '10px',
              color: '#111',
              cursor: 'pointer',
            }}
          >
            Share to Instagram Story
          </button>
        )}

        {!isExporting && (
          <button
            onClick={handleDownload}
            style={{
              position: 'absolute',
              bottom: '20px',
              padding: '12px 24px',
              fontSize: '18px',
              backgroundColor: '#f5a',
              border: 'none',
              borderRadius: '8px',
              color: '#111',
              cursor: 'pointer',
            }}
          >
            Download & Share
          </button>
        )}
      </div>
    </div>
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: '-200vw', // 👈 fuera de pantalla
        width: '1080px',
        height: '1920px',
        pointerEvents: 'none',
        zIndex: -1,
      }}
    >
      <div
        ref={containerRef}
        className="gp-wrapped"
        style={{
          width:'1080px',
          height: '1920px',
          backgroundImage: 'url(/img/brand/gp_loto_phone.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: '#fff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* 🔥 BACKGROUND OVERLAY */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(
                to bottom,
                rgba(0,0,0,0) 45%,
                rgba(0,0,0,0.35) 70%,
                rgba(0,0,0,0.8) 100%
              )
            `,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        {/* CONTENT */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <style>{`
            .bold { font-weight: 700 !important; }
          `}</style>
          
          {/* HEADER */}
          <div className='text-center mb-3'>
            <div
              style={{
                marginTop: '300px',
              }}
            >
            </div>
            <h1 style={{fontSize: '128px', lineHeight: '128px'}}>
              Wrapped {year}
            </h1>
          </div>

          {/* MAIN STAT */}
          <div className='text-center' style={{marginTop: '80px'}}>
            <h1 className='bold' style={{fontSize: '128px', lineHeight: '128px'}}>
              {totalClasses}
            </h1>
            <h3 style={{fontSize: '64px', lineHeight: '64px', marginTop: '50px'}}>
              Clases tomadas
            </h3>
          </div>

          {/* HIGHLIGHT CARDS */}
          <div
            style={{
              width: '80%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridGap: '80px',
              marginTop: '100px',
            }}
          >
            <ExportableCard title="Clase Favorita" value={favoriteClass} footer={favoriteClassCount + " clases"} />
            <ExportableCard title="Mejor Mes" value={topMonth} />
            {stats.best_ranking < 10 && (
              <ExportableCard
                title="Mejor Ranking"
                value={bestRanking}
                style={{ gridColumn: '1 / -1', justifySelf: 'center' }}
              />
            )}
          </div>

          {/* FOOTER */}
          <div
            style={{
              position: 'absolute',
              bottom: '30px',
              textAlign: 'center',
              width: '100%',
              fontSize: '68px',
              color: 'white',
            }}
          >
            Gracias por ser parte de la comunidad GP 💗
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function Card({ title, value, footer, style }) {
  const baseStyle = {
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',    
    height: '170px',
    width: '170px',
    margin: 'auto',
    textAlign: 'center',
    color: 'var(--theme-primary-solid)',
    justifyContent: 'space-evenly',
  };

  return (
    <div className='themed-block' style={{ ...baseStyle, ...(style || {}) }}>
      <h4>{title}</h4>
      <h3 className='bold'>{value}</h3>
      <h5>{footer}</h5>
    </div>
  );
}

// Small reusable card component
function ExportableCard({ title, value, footer, style }) {
  const baseStyle = {
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',    
    height: '360px',
    width: '360px',
    margin: 'auto',
    textAlign: 'center',
    color: 'var(--theme-primary-solid)',
    justifyContent: 'space-evenly',
  };

  const canvasSafeCardStyle = {
    backgroundColor: '#f9f6f8', // solid (no alpha)
    borderRadius: '16px',
  };

  return (
    <div style={{ 
      ...baseStyle,
      ...canvasSafeCardStyle,
      ...(style || {})
    }}>
  	  <h4 className='bold' style={{fontSize: '56px', lineHeight: '56px'}}>{title}</h4>
      <h3 className='bold' style={{fontSize: '64px', lineHeight: '64px'}}>{value}</h3>
      <h5 className='bold' style={{fontSize: '48px', lineHeight: '48px'}}>{footer}</h5>
    </div>
  );
}
